/**
 * The arsenal (group "shop", see ./shop.ts): selling, upgrading, equipping and using what the player owns.
 *
 * Original UI (new_interf render 1817-2010): the arsenal lists every owned entry of the current tab (weapons: one
 * entry per distinct (range, stolen, crafted) copy, named 'weapon_<id>%<s|c|>%<range>'; other tabs: entries with a
 * count != 0, never flag_russia / flag_ussr, named 'weapon_<id>%'). Hovering an entry shows its weapon_functions clip,
 * whose frame decides the buttons:
 *   frame 1: arsenal_upgrade + arsenal_sell  - weapons with DEF.allow_upg and range != 10
 *   frame 2: arsenal_sell                    - other weapons, non-usable objects, the implant currently worn
 *   frame 3: arsenal_use                     - stimulants, flags, graves (NO sell button)
 *   frame 4: arsenal_use + arsenal_sell      - implants not worn, usable objects (the books)
 *   frame 5: nothing                         - the flag / grave currently worn, stimulants once the battle limit is
 *                                              reached, the auction annihilator
 */
import { argsObject, intArg, knownKey, stringArg } from '../args';
import { int, keys } from '../as3';
import { RuleError } from '../errors';
import { defineOp } from '../registry';
import type { PlayerState } from '../state';
import {
  BOOK_COLLECTION_GOLD,
  BOOK_IDS,
  CATEGORY_TABLES,
  findCopy,
  idArg,
  perkEffect,
  rangeUpgrade,
  sellPrice,
  weaponOptimisation,
  type ShopCategory,
} from './shop-common';

/** new_interf.MAX_STIM (new_interf.ts:68): stimulants a team may take into one battle before the Stims perk. */
export const MAX_STIM = 4;

/** Crafted weapon copies that may be sold per day (render 4003-4008). */
export const CRAFT_SELLS_PER_DAY = 3;

/** The quick-weapon keys of player.quick_weapons (the 'wpclear_<key>' controls). */
export const QUICK_KEYS = ['z', 'x', 'c', 'v', 'b', 'n', 'm', 'k', 'l'] as const;
export type QuickKey = (typeof QUICK_KEYS)[number];

export type SellCategory = Exclude<ShopCategory, 'weapons'>;
export const SELL_CATEGORIES: readonly SellCategory[] = ['help', 'flags', 'rip', 'impl', 'objs'];
export type EquipCategory = 'flags' | 'rip' | 'impl';
export const EQUIP_CATEGORIES: readonly EquipCategory[] = ['flags', 'rip', 'impl'];

/** The copy marker of an arsenal weapon entry: 's' stolen, 'c' crafted, '' neither. */
export type CopyStat = '' | 's' | 'c';
export const COPY_STATS: readonly CopyStat[] = ['', 's', 'c'];

const CURRENT_FIELD: Readonly<Record<EquipCategory, 'cur_flag' | 'cur_rip' | 'cur_impl'>> = { flags: 'cur_flag', rip: 'cur_rip', impl: 'cur_impl' };

/** The arsenal lists a non-weapon entry: a count != 0 (render 1887); the rules require a positive count. */
function ownedCount(store: Record<string, any>, id: string): number {
  const n = Number(store[id]);
  if (!(n > 0)) throw new RuleError('not_owned', { id, count: store[id] ?? 0 });
  return n;
}

/**
 * The copy of weapons[id] with this (stat, range) that the arsenal lists (weapons[id][0] != 0, id != 'skip_go',
 * render 1860-1861) and finish_sell / finish_upgrade find (findCopy); RuleError('not_owned') when there is none.
 */
function listedCopy(p: PlayerState, id: string, stat: CopyStat, range: number): number {
  const w = p.weapons[id];
  const index = id == 'skip_go' || !w || w[0] == 0 ? 0 : findCopy(p, id, stat, range);
  if (index == 0) throw new RuleError('not_owned', { id, stat, range });
  return index;
}

/** The highest rank the player may upgrade to (render 3960-3972: 4, Crafting 1/2/3 -> 6/8/10, specialisation 4 -> 10). */
export function maxUpgradeRank(p: PlayerState): number {
  let max = 4;
  if (p.perks && p.perks['crafting']) {
    if (p.perks['crafting'] == 1) max = 6;
    if (p.perks['crafting'] == 2) max = 8;
    if (p.perks['crafting'] == 3) max = 10;
  }
  if (p.spec == 4) max = 10;
  return max;
}

/** The upgrade price render computes into weapon_stack.upg_price (new_interf 3950-3951). */
export function upgradePrice(def: any, range: number): number {
  return Math.round(rangeUpgrade(range + 1, 'price', def.wpclass) * def.gold);
}

/** The stimulants already taken into the next battle: sum of stim_to_fight over STIM_DEF (render 1956-1960). */
export function stimsToFight(p: PlayerState, stimDef: Record<string, unknown>): number {
  let sum = 0;
  for (const k of keys(stimDef)) {
    if (p.stim_to_fight[k as string]) sum += p.stim_to_fight[k as string];
  }
  return sum;
}

export interface ArsenalSellArgs {
  category: SellCategory;
  id: string;
}

export interface ArsenalSellResult {
  category: SellCategory;
  id: string;
  /** Gold credited. */
  gold: number;
  /** Pieces left (0 for the book collection). */
  count: number;
  /** The nine books were sold as a collection. */
  bookCollection: boolean;
}

export interface WeaponCopyArgs {
  id: string;
  stat: CopyStat;
  range: number;
}

export interface SellWeaponResult {
  id: string;
  gold: number;
  /** Copies left. */
  count: number;
}

export interface UpgradeResult {
  id: string;
  /** Gold paid. */
  price: number;
  /** The new rank of the copy. */
  range: number;
}

export interface StimToFightResult {
  id: string;
  /** stim_to_fight[id] and stim_behave[id] after the move. */
  toFight: number;
  left: number;
}

export interface EquipArgs {
  category: EquipCategory;
  id: string;
}

export interface ReadBookResult {
  id: string;
  /** CRAFT_DEF indexes of the newly learnt recipes. */
  recipes: number[];
}

function copyArgs(raw: unknown): WeaponCopyArgs {
  const o = argsObject(raw, ['id', 'stat', 'range']);
  return { id: idArg(o), stat: stringArg(o, 'stat', { oneOf: COPY_STATS }), range: intArg(o, 'range', { min: 1, max: 10 }) };
}

export const arsenalOps = {
  /**
   * arsenal.sell { category, id }: 'arsenal_sell' of a non-weapon entry (new_interf 5004-5082).
   * Preconditions: a sell button exists (implants and objects only: stimulants, flags and graves show frame 3/5,
   * which has none -> 'not_sellable'), and the entry is listed (count > 0). A book is sold only as the collection:
   * all nine books exactly once -> 3 000 000 gold, otherwise 'collect_all_books' (the original's error screen).
   * Mutations: count - 1, gold += get_sell_price (implants the full gold, objects floor(gold * (0.3 + Barter))); when
   * the count reaches 0, cur_impl = 0 even if another implant is worn (quirk of the original, kept).
   */
  'arsenal.sell': defineOp<ArsenalSellArgs, ArsenalSellResult>({
    parseArgs(raw) {
      const o = argsObject(raw, ['category', 'id']);
      return { category: stringArg(o, 'category', { oneOf: SELL_CATEGORIES }), id: idArg(o) };
    },
    run(state, { category, id }, ctx) {
      const p = state.player;
      const { def: defName, store: storeName } = CATEGORY_TABLES[category];
      const table: Record<string, any> = ctx.defs[defName];
      knownKey(table, id);
      if (category == 'help' || category == 'flags' || category == 'rip') throw new RuleError('not_sellable', { category, id });
      const store: Record<string, any> = p[storeName];
      ownedCount(store, id);
      if ((BOOK_IDS as readonly string[]).includes(id)) {
        const o = p.objs;
        if (BOOK_IDS.every((b) => o[b] == 1)) {
          for (const b of BOOK_IDS) o[b] = 0;
          p.gold = int(p.gold + BOOK_COLLECTION_GOLD);
          return { state, result: { category, id, gold: BOOK_COLLECTION_GOLD, count: 0, bookCollection: true } };
        }
        throw new RuleError('collect_all_books', { missing: BOOK_IDS.filter((b) => o[b] != 1) });
      }
      --store[id];
      const gold = sellPrice(ctx.defs, p, category, id);
      p.gold = int(p.gold + gold);
      if (store[id] == 0) {
        // flags / rip never get here (not sellable); the original resets them the same way.
        if (category == 'impl') p.cur_impl = 0;
      }
      return { state, result: { category, id, gold, count: store[id], bookCollection: false } };
    },
  }),

  /**
   * arsenal.sellWeapon { id, stat, range }: 'arsenal_sell' of a weapon entry opens sell_weapon, 'finish_sell'
   * (new_interf 5083-5114) sells the first copy with exactly this range and stat.
   * Preconditions: the copy is listed; a crafted copy only while today_craft_sells < 3 (render 4003-4008 hides
   * finish_sell).
   * Mutations: count - 1, the copy becomes {} (dropped by weapon_optimisation), gold += get_sell_price(id, range,
   * stolen, crafted) (stolen 50; crafted floor(k * gold * (0.2 + Barter)); Kenton weapons full gold; rank multiplier
   * get_range_upgrade(range, 'sell_price')), today_craft_sells + 1 for a crafted copy, weapon_optimisation.
   */
  'arsenal.sellWeapon': defineOp<WeaponCopyArgs, SellWeaponResult>({
    parseArgs: copyArgs,
    run(state, { id, stat, range }, ctx) {
      const p = state.player;
      knownKey(ctx.defs.WEAPONS_DEF, id);
      const index = listedCopy(p, id, stat, range);
      const stolen = stat == 's';
      const crafted = stat == 'c';
      if (crafted && p.today_craft_sells! >= CRAFT_SELLS_PER_DAY) throw new RuleError('limit_reached', { what: 'crafted_sells', limit: CRAFT_SELLS_PER_DAY, have: p.today_craft_sells });
      const w = p.weapons[id];
      --w[0];
      w[index] = {};
      const gold = sellPrice(ctx.defs, p, 'weapons', id, range, stolen, crafted);
      p.gold = int(p.gold + gold);
      if (crafted) (p as any).today_craft_sells = (p.today_craft_sells as number) + 1;
      weaponOptimisation(p.weapons);
      return { state, result: { id, gold, count: p.weapons[id][0] } };
    },
  }),

  /**
   * arsenal.upgrade { id, stat, range }: 'arsenal_upgrade' opens upgrade_weapon, 'finish_upgrade' (new_interf
   * 5133-5173) raises the first copy with exactly this range and stat by one rank.
   * Preconditions (frame 1 and render 3928-3984): the copy is listed, DEF.allow_upg, range != 10, the price
   * round(get_range_upgrade(range + 1, 'price', wpclass) * gold) <= gold, range + 1 <= maxUpgradeRank.
   * Mutations: range + 1, gold -= price, task 17 -> 18, achiev.upgraded++ for class > 1, weapon_optimisation.
   */
  'arsenal.upgrade': defineOp<WeaponCopyArgs, UpgradeResult>({
    parseArgs: copyArgs,
    run(state, { id, stat, range }, ctx) {
      const p = state.player;
      knownKey(ctx.defs.WEAPONS_DEF, id);
      const def: any = ctx.defs.WEAPONS_DEF[id];
      const index = listedCopy(p, id, stat, range);
      if (!Boolean(def.allow_upg)) throw new RuleError('not_upgradable', { id });
      if (range == 10) throw new RuleError('max_rank', { id, range });
      const price = upgradePrice(def, range);
      if (price > p.gold) throw new RuleError('not_enough_gold', { have: p.gold, need: price });
      const max = maxUpgradeRank(p);
      if (range + 1 > max) {
        let need = 1;
        if (range + 1 > 6) need = 2;
        if (range + 1 > 8) need = 3;
        throw new RuleError('crafting_too_low', { need, rank: range + 1, max });
      }
      const copy = p.weapons[id][index];
      ++copy.range;
      p.gold = int(p.gold - price);
      if (p.cur_task == 17) p.cur_task = 18;
      if (def.wpclass > 1) {
        if (p.achiev['upgraded']) ++p.achiev['upgraded'];
        else p.achiev['upgraded'] = 1;
      }
      weaponOptimisation(p.weapons);
      return { state, result: { id, price, range: range + 1 } };
    },
  }),

  /**
   * arsenal.stimToFight { id }: 'arsenal_use' on a stimulant (new_interf 5194-5204).
   * Preconditions: the stimulant is listed (stim_behave[id] > 0) and fewer than MAX_STIM + Stims effect are taken
   * (render 1952-1963 shows frame 5 without the button at the limit).
   * Mutations: stim_to_fight[id] + 1, stim_behave[id] - 1.
   */
  'arsenal.stimToFight': defineOp<{ id: string }, StimToFightResult>({
    parseArgs(raw) {
      const o = argsObject(raw, ['id']);
      return { id: idArg(o) };
    },
    run(state, { id }, ctx) {
      const p = state.player;
      knownKey(ctx.defs.STIM_DEF, id);
      ownedCount(p.stim_behave, id);
      let bonus = 0;
      if (p.perks && p.perks['stims']) bonus = perkEffect('stims', p.perks.stims);
      const taken = stimsToFight(p, ctx.defs.STIM_DEF);
      if (taken >= MAX_STIM + bonus) throw new RuleError('limit_reached', { what: 'stims', limit: MAX_STIM + bonus, have: taken });
      if (p.stim_to_fight[id]) ++p.stim_to_fight[id];
      else p.stim_to_fight[id] = 1;
      --p.stim_behave[id];
      return { state, result: { id, toFight: p.stim_to_fight[id], left: p.stim_behave[id] } };
    },
  }),

  /**
   * arsenal.equip { category: flags|rip|impl, id }: 'arsenal_use' (new_interf 5205-5213) wears the hovered entry
   * (cur_element = DEF.id, mouse_event 6472-6479).
   * Preconditions: the entry is listed (count > 0; never flag_russia / flag_ussr) and not worn already (the worn flag /
   * grave shows frame 5, the worn implant frame 2 - no use button).
   * Mutations: cur_flag / cur_rip / cur_impl = DEF.id.
   */
  'arsenal.equip': defineOp<EquipArgs, { category: EquipCategory; id: string; current: number }>({
    parseArgs(raw) {
      const o = argsObject(raw, ['category', 'id']);
      return { category: stringArg(o, 'category', { oneOf: EQUIP_CATEGORIES }), id: idArg(o) };
    },
    run(state, { category, id }, ctx) {
      const p = state.player;
      const { def: defName, store: storeName } = CATEGORY_TABLES[category];
      const table: Record<string, any> = ctx.defs[defName];
      knownKey(table, id);
      if (id == 'flag_russia' || id == 'flag_ussr') throw new RuleError('not_available', { id, reason: 'not_listed' });
      ownedCount(p[storeName], id);
      const field = CURRENT_FIELD[category];
      if (table[id].id == p[field]) throw new RuleError('already_equipped', { id });
      p[field] = table[id].id;
      return { state, result: { category, id, current: p[field] } };
    },
  }),

  /**
   * arsenal.readBook { id }: 'arsenal_use' on a usable object (new_interf 5176-5192): every recipe of
   * BOOKS_DEF[id].param (FWP.get_book_recipes) that is still 0 becomes 1; the book is kept.
   * Preconditions: the book is listed (objs[id] > 0) and usable (frame 4; only the books are), and it teaches at least
   * one new recipe (otherwise the original shows 'new_recipes_fail' and saves nothing).
   */
  'arsenal.readBook': defineOp<{ id: string }, ReadBookResult>({
    parseArgs(raw) {
      const o = argsObject(raw, ['id']);
      return { id: idArg(o) };
    },
    run(state, { id }, ctx) {
      const p = state.player;
      knownKey(ctx.defs.OBJ_DEF, id);
      ownedCount(p.objs, id);
      if (!ctx.defs.OBJ_DEF[id].usable || !Object.prototype.hasOwnProperty.call(ctx.defs.BOOKS_DEF, id)) throw new RuleError('not_usable', { id });
      const param = ctx.defs.BOOKS_DEF[id].param;
      const learnt: number[] = [];
      for (let i = 1; i <= param[0]; i++) {
        const r = param[i];
        if (p.recipes[r] == 0 && !learnt.includes(r)) learnt.push(r);
      }
      if (learnt.length == 0) throw new RuleError('no_new_recipes', { id });
      for (const r of learnt) p.recipes[r] = 1;
      return { state, result: { id, recipes: learnt } };
    },
  }),

  /**
   * arsenal.clearQuickSlot { key }: 'wpclear_<key>' (new_interf 5490-5494): quick_weapons[key] = 0. The controls sit
   * in the arsenal_quick_zone clip, which no menu of the interface places (tests/integration/meta-economy.test.ts), so
   * the op keeps the handler for completeness; the key must be one of the nine quick keys.
   */
  'arsenal.clearQuickSlot': defineOp<{ key: QuickKey }, { key: QuickKey }>({
    parseArgs(raw) {
      const o = argsObject(raw, ['key']);
      return { key: stringArg(o, 'key', { oneOf: QUICK_KEYS }) };
    },
    run(state, { key }) {
      state.player.quick_weapons[key] = 0;
      return { state, result: { key } };
    },
  }),
};
